import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = Cookies.get('auth_token');
      console.log('AuthContext: Initializing auth, token exists:', !!token);
      
      if (token) {
        try {
          const userData = await authService.getCurrentUser();
          console.log('AuthContext: User data received:', userData);
          
          // Allow affiliate and admin roles for affiliate portal
          if (userData.role === 'affiliate' || userData.role === 'admin') {
            setUser(userData);
            setIsAuthenticated(true);
            console.log('AuthContext: User authenticated as:', userData.role);
          } else {
            // Not an allowed role, clear token
            console.log('AuthContext: User role not allowed:', userData.role);
            Cookies.remove('auth_token');
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          Cookies.remove('auth_token');
        }
      } else {
        console.log('AuthContext: No token found, user not authenticated');
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      if (response.user.role !== 'affiliate' && response.user.role !== 'admin') {
        throw new Error('Access denied. This portal is for affiliates and admins only.');
      }
      
      setUser(response.user);
      setIsAuthenticated(true);
      Cookies.set('auth_token', response.token, { expires: 7 }); // 7 days
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    Cookies.remove('auth_token');
  };

  const registerCommenter = async (userData) => {
    try {
      // Only allow affiliate users to be authenticated in the portal
      if (userData.role === 'commenter') {
        // Commenter users should not be authenticated in the portal
        setUser(null);
        setIsAuthenticated(false);
        return userData;
      }
      
      setUser(userData);
      setIsAuthenticated(true);
      // Token should already be set by the registration form
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    setUser,
    loading,
    isAuthenticated,
    login,
    logout,
    registerCommenter
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
